/*
 * Feed Medic report — the shape the renderer consumes.
 *
 * This is deliberately typed against what the report *renders*, not against
 * what FeedMedicRun persists. The stored result carries fields the report has
 * never shown and must never publish — notably `ourFetch.externalFetch`, which
 * holds the entire proxied feed body (ExternalDualProxyClient::fetch), and
 * `proxyFallback`, which reveals the paid alternate-egress path. The share page
 * is unauthenticated and forwardable, so `FeedMedicService::toPublicPayload()`
 * is built to satisfy *this* contract rather than this file being widened to
 * describe the stored blob. Adding a field here is a decision to publish it.
 */

/* ---------------------------------------------------------------- severity */

/*
 * Three distinct vocabularies overlap in this report, and collapsing them into
 * one enum would misrepresent where each value comes from.
 */

/** Emitted per finding by the check classes. */
export type FindingSeverity = 'ok' | 'info' | 'warning' | 'critical' | 'skipped';

/**
 * Bucket status, as stored. `incomplete` means the bucket never ran — either
 * because it isn't its turn yet (live run) or because an earlier fatal check
 * short-circuited the run (terminal run).
 */
export type BucketStatus = 'complete' | 'running' | 'incomplete' | 'skipped' | 'error';

/**
 * What a bucket header displays. Derived from BucketStatus plus the findings —
 * never stored. `pending` and `running` exist only mid-run; `error` status
 * presents as `critical` because the captured findings are still the useful
 * artifact.
 */
export type BucketDisplayState = 'pending' | 'running' | 'skipped' | 'ok' | 'warning' | 'critical';

/** Run-level outcome, set only when the run closes. */
export type Verdict = 'healthy' | 'warnings' | 'critical' | 'error';

/** Run lifecycle, from the service envelope. */
export type RunStatus = 'queued' | 'running' | 'done' | 'error';

export const BUCKET_NAMES = ['accessibility', 'content', 'social', 'settings'] as const;

export type BucketName = (typeof BUCKET_NAMES)[number];

/* ---------------------------------------------------------------- findings */

export interface Finding {
    key: string;
    severity: FindingSeverity;
    /**
     * SettingsReviewCheck emits `message` where the other three checks emit
     * `headline`. Both are load-bearing — read them as `headline ?? message`.
     */
    headline?: string;
    message?: string;
    detail?: string;
    fatal?: boolean;
}

interface BucketBase {
    status: BucketStatus;
    summary?: string;
    findings?: Finding[];
    /** Present when status is `skipped`. */
    reason?: string;
    /** Present when status is `error`. */
    error?: string;
    fatal?: boolean;
}

/* ----------------------------------------------------------- accessibility */

export interface ResponseHeaders {
    [name: string]: string;
}

export interface OurFetch {
    httpCode?: number;
    server?: string;
    contentType?: string;
    /** Bytes; the renderer converts to KB. */
    feedSize?: number;
    headers?: ResponseHeaders;
}

export interface Caching {
    cacheControl?: string;
    age?: number | null;
    xCache?: string;
    etag?: string;
    expires?: string;
}

export interface AccessibilityBucket extends BucketBase {
    finalUrl?: string;
    httpUpgraded?: boolean;
    ourFetch?: OurFetch;
    caching?: Caching;
}

/* ----------------------------------------------------------------- content */

export interface ImageInfo {
    url?: string;
    thumbUrl?: string;
    reachable?: boolean;
    bytes?: number;
    width?: number;
    height?: number;
    format?: string;
}

export interface ContentItem {
    link?: string;
    title?: string;
    og?: {title?: string; description?: string};
    twitter?: {description?: string};
    image?: ImageInfo;
    feedMedia?: ImageInfo[];
    /** Names of absent meta tags, e.g. ['twitter:title', 'twitter:image']. */
    missing?: string[];
    pageFetched?: boolean;
    proxySkipped?: boolean;
    cloudflareChallenge?: boolean;
    accessibleViaProxyOnly?: boolean;
}

export interface ContentBucket extends BucketBase {
    items?: ContentItem[];
    itemsSampled?: number;
    itemsTotal?: number;
}

/* ------------------------------------------------------------------ social */

/**
 * Outcome buckets are keyed by the stored slugs, which are not identifiers —
 * they must be quoted and read with bracket access.
 */
export interface Destination {
    paId: number;
    name?: string;
    service?: string;
    ok?: number;
    'dlvr-throttled'?: number;
    'platform-rejected'?: number;
    'subscription-limited'?: number;
    'other-error'?: number;
}

export interface SocialBucket extends BucketBase {
    perDestination?: Destination[];
    lookbackDays?: number;
    note?: string;
}

/* ---------------------------------------------------------------- settings */

export interface FilterClause {
    /** 'all' | 'any' | 'none' — rendered verbatim, so kept as a string. */
    clause: string;
    rules: string[];
}

export interface FilterReport {
    definition?: {clauses?: FilterClause[]};
    sampled?: number;
    passed?: number;
    failed?: number;
    failedTitles?: string[];
}

export interface SettingsBucket extends BucketBase {
    filterReport?: FilterReport;
}

/* ------------------------------------------------------------------ result */

export interface MedicResult {
    schemaVersion?: number;
    /** Set only on terminal close — its absence is how the UI tells pending from skipped. */
    verdict?: Verdict;
    feedUrl?: string;
    finalUrl?: string;
    httpUpgraded?: boolean;
    accessibility?: AccessibilityBucket;
    content?: ContentBucket;
    social?: SocialBucket;
    settings?: SettingsBucket;
}

/** Map of bucket name to its concrete type, so renderers stay narrow. */
export interface BucketByName {
    accessibility: AccessibilityBucket;
    content: ContentBucket;
    social: SocialBucket;
    settings: SettingsBucket;
}

/* ---------------------------------------------------------------- progress */

export type ProgressPhase = 'init' | BucketName;

export interface MedicProgress {
    status?: string;
    phase?: ProgressPhase | string;
    phasePct?: number;
    /** 0..1; the renderer converts to a percentage. */
    overallPct?: number;
    msg?: string;
}

/* -------------------------------------------------------------------- runs */

export interface MedicRun {
    runId?: number;
    token?: string;
    status?: RunStatus;
    verdict?: Verdict;
    feedUrl?: string;
    /** 'Y-m-d H:i:s' in UTC — DateDisplay is given an explicit '+0000'. */
    createdTs?: string;
    completedTs?: string;
    durationMs?: number;
    result?: MedicResult;
    errorMsg?: string;
    progress?: MedicProgress;
    /** Transient notice, e.g. another run already in flight. */
    message?: string;
}

/** Row shape for the previous-runs list. */
export interface MedicRunSummary {
    token: string;
    createdTs?: string;
    verdict?: Verdict;
    status?: RunStatus;
    durationMs?: number;
}

/** What the share page's JSON element carries. */
export interface MedicSharePayload {
    run: MedicRun;
}
